import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { PageHeader, LoadingTable, ErrorState, EmptyState } from '../components/States';
import { DataTable } from '../components/DataTable';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Card } from '../components/ui/card';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '../components/ui/alert-dialog';
import { useAuth } from '../context/AuthContext';
import * as api from '../lib/api';
import { formatCurrency } from '../lib/format';
import { Plus, Trash2, Play, Sparkles, Wand2 } from 'lucide-react';

export default function AnalisiCGRegole() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'control'; // sezione Analisi CG aperta al Controllo di gestione
  const [schemas, setSchemas] = useState([]);
  const [selSchema, setSelSchema] = useState('');
  const [rules, setRules] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [ruleDlg, setRuleDlg] = useState(null);
  const [dry, setDry] = useState(null);
  const [dryCompany, setDryCompany] = useState('all');
  const [genConfirm, setGenConfirm] = useState(false);
  const [generating, setGenerating] = useState(false);

  const loadBase = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const [s, c] = await Promise.all([api.cgaSchemas(), api.cgaCompanies()]);
      setSchemas(s); setCompanies(c);
      if (s.length && !selSchema) setSelSchema(s[0].id);
    } catch (e) { setErr(api.apiError(e)); } finally { setLoading(false); }
  }, [selSchema]);
  useEffect(() => { loadBase(); }, []); // eslint-disable-line

  const loadRules = useCallback(async (sid) => {
    if (!sid) { setRules([]); return; }
    try { setRules(await api.cgaRules(sid)); } catch (e) { toast.error(api.apiError(e)); }
  }, []);
  useEffect(() => { loadRules(selSchema); setDry(null); }, [selSchema, loadRules]);

  const schema = schemas.find((s) => s.id === selSchema);
  const rowKeys = (schema?.rows || []).map((r) => r.key);

  async function createDefault() {
    try { const s = await api.cgaSchemaCreateDefault(); toast.success('Schema CE di default creato'); setSchemas([...schemas, s]); setSelSchema(s.id); }
    catch (e) { toast.error(api.apiError(e)); }
  }
  async function deleteSchema() {
    try { const r = await api.cgaSchemaDelete(selSchema); toast.success(`Schema eliminato (${r.deleted_rules} regole)`); setSelSchema(''); loadBase(); }
    catch (e) { toast.error(api.apiError(e)); }
  }
  async function saveRule(form) {
    try {
      const payload = { schema_id: selSchema, priority: Number(form.priority) || 100, active: form.active,
        match: buildMatch(form), action: { target_row_key: form.target_row_key, sign: Number(form.sign), intercompany_flag: false } };
      if (ruleDlg?.id) await api.cgaRuleUpdate(ruleDlg.id, payload);
      else await api.cgaRuleCreate(payload);
      toast.success('Regola salvata'); setRuleDlg(null); loadRules(selSchema);
    } catch (e) { toast.error(api.apiError(e)); }
  }
  async function delRule(r) {
    try { await api.cgaRuleDelete(r.id); toast.success('Regola eliminata'); loadRules(selSchema); } catch (e) { toast.error(api.apiError(e)); }
  }
  async function runDryRun() {
    try {
      const p = { schema_id: selSchema };
      if (dryCompany !== 'all') p.company_id = dryCompany;
      const res = await api.cgaDryRun(p); setDry(res);
      toast.success(`Anteprima: ${res.meta.entries_analyzed} righe analizzate`);
    } catch (e) { toast.error(api.apiError(e)); }
  }
  async function autoGenerate() {
    setGenerating(true);
    try {
      const p = { schema_id: selSchema, replace_existing: true };
      if (dryCompany !== 'all') p.company_ids = [dryCompany];
      const res = await api.cgaAutoGenerateRules(p);
      toast.success(`Generate ${res.created} regole (${res.accounts_mapped}/${res.accounts_total} conti mappati)`);
      setGenConfirm(false);
      loadRules(selSchema);
      setDry(null);
    } catch (e) { toast.error(api.apiError(e)); } finally { setGenerating(false); }
  }

  if (loading) return <div><PageHeader title="Schemi & Regole" /><LoadingTable /></div>;
  if (err) return <div><PageHeader title="Schemi & Regole" /><ErrorState message={err} onRetry={loadBase} /></div>;

  return (
    <div data-testid="cga-regole-page">
      <PageHeader title="Schemi & Regole di riclassifica" description="Schema riclassificato e regole della Base 2025 (inizializzati automaticamente dal sistema)" />

      {schemas.length === 0 ? (
        <EmptyState title="Nessuno schema" description="Lo schema riclassificato viene inizializzato automaticamente dal sistema all'avvio." />
      ) : (
        <>
          <div className="flex flex-wrap items-end gap-3 mb-4">
            <div className="w-80"><Label>Schema attivo</Label>
              <Select value={selSchema} onValueChange={setSelSchema}>
                <SelectTrigger data-testid="schema-select"><SelectValue /></SelectTrigger>
                <SelectContent>{schemas.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} ({s.type})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {isAdmin && <Button variant="outline" size="sm" className="text-destructive" onClick={deleteSchema} data-testid="delete-schema-btn"><Trash2 className="h-4 w-4 mr-1.5" />Elimina schema</Button>}
          </div>

          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold">Regole ({rules.length})</h3>
            {isAdmin && (
              <div className="flex gap-2">
                <Button size="sm" onClick={() => setRuleDlg({})} data-testid="add-rule-btn"><Plus className="h-4 w-4 mr-1.5" />Nuova regola</Button>
              </div>
            )}
          </div>
          <DataTable
            rows={rules}
            columns={[
              { key: 'priority', header: 'Priorità', align: 'right' },
              { key: 'match', header: 'Condizione', sortable: false, render: (r) => <span className="text-xs">{matchSummary(r.match)}</span> },
              { key: 'target', header: 'Voce destinazione', sortable: false, render: (r) => <Badge variant="outline">{r.action?.target_row_key}</Badge> },
              { key: 'sign', header: 'Segno', sortable: false, render: (r) => r.action?.sign === -1 ? '−' : '+' },
              { key: 'active', header: 'Attiva', render: (r) => r.active ? <Badge variant="secondary">Sì</Badge> : <Badge variant="outline">No</Badge> },
            ]}
            actions={{ canWrite: isAdmin, onEdit: (r) => setRuleDlg(r), onDelete: delRule }}
            emptyText="Nessuna regola. Aggiungi regole per mappare i conti."
          />

          {/* Dry-run */}
          <Card className="mt-6 p-4">
            <div className="flex flex-wrap items-end gap-3">
              <div className="w-64"><Label>Anteprima su società</Label>
                <Select value={dryCompany} onValueChange={setDryCompany}>
                  <SelectTrigger data-testid="dryrun-company-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutte le società</SelectItem>
                    {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.code} — {c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={runDryRun} data-testid="run-dryrun-btn"><Play className="h-4 w-4 mr-1.5" />Anteprima riclassifica (dry-run)</Button>
            </div>
            {dry && (
              <div className="mt-4" data-testid="dryrun-result">
                <div className="overflow-x-auto rounded-lg border">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40"><tr><th className="text-left p-2 text-xs font-semibold text-muted-foreground">Voce</th><th className="text-right p-2 text-xs font-semibold text-muted-foreground">Totale</th></tr></thead>
                    <tbody>
                      {dry.rows.map((r) => <tr key={r.key} className="border-t"><td className={`p-2 ${r.computed ? 'font-semibold' : ''}`}>{r.label}</td><td className="p-2 text-right tabular-nums">{formatCurrency(r.total)}</td></tr>)}
                      <tr className="border-t bg-muted/30"><td className="p-2 text-[hsl(var(--warning-foreground))]">Da classificare ({dry.unmatched.count} conti)</td><td className="p-2 text-right tabular-nums">{formatCurrency(dry.unmatched.total)}</td></tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </Card>
        </>
      )}

      {ruleDlg && <RuleDialog data={ruleDlg} rowKeys={rowKeys} companies={companies} onClose={() => setRuleDlg(null)} onSave={saveRule} />}

      <AlertDialog open={genConfirm} onOpenChange={(o) => !o && setGenConfirm(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Generare le regole con l'AI?</AlertDialogTitle>
            <AlertDialogDescription>
              L'AI analizzerà i conti presenti nei mastrini caricati{dryCompany !== 'all' ? ' della società selezionata' : ' di tutte le società'} e creerà automaticamente le regole di riclassifica, mappandoli sulle voci dello schema "{schema?.name}". Le regole verranno <strong>attivate subito</strong> e <strong>sostituiranno</strong> quelle esistenti dello schema.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="gen-cancel-btn">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={autoGenerate} disabled={generating} data-testid="gen-confirm-btn">{generating ? 'Generazione…' : 'Genera'}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function buildMatch(f) {
  const m = {};
  if (f.code_prefix) m.code_prefix = f.code_prefix.trim();
  if (f.keyword) m.keyword = f.keyword.trim();
  if (f.nature && f.nature !== 'any') m.nature = f.nature;
  if (f.intercompany_only) m.intercompany_only = true;
  return m;
}
function matchSummary(m) {
  if (!m || Object.keys(m).length === 0) return 'Tutti (catch-all)';
  const parts = [];
  if (m.code_prefix) parts.push(`codice inizia con "${m.code_prefix}"`);
  if (m.keyword) parts.push(`descrizione contiene "${m.keyword}"`);
  if (m.nature) parts.push(`natura=${m.nature}`);
  if (m.intercompany_only) parts.push('solo intercompany');
  return parts.join(' · ');
}

function RuleDialog({ data, rowKeys, onClose, onSave }) {
  const [f, setF] = useState({
    priority: data.priority ?? 100,
    code_prefix: data.match?.code_prefix || '',
    keyword: data.match?.keyword || '',
    nature: data.match?.nature || 'any',
    intercompany_only: data.match?.intercompany_only || false,
    target_row_key: data.action?.target_row_key || (rowKeys[0] || ''),
    sign: data.action?.sign ?? 1,
    active: data.active ?? true,
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="form-modal">
        <DialogHeader><DialogTitle>{data.id ? 'Modifica regola' : 'Nuova regola'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Priorità (0 = massima)</Label><Input type="number" value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })} data-testid="rule-priority-input" /></div>
            <div><Label>Segno</Label>
              <Select value={String(f.sign)} onValueChange={(v) => setF({ ...f, sign: Number(v) })}>
                <SelectTrigger data-testid="rule-sign-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="1">Positivo (+)</SelectItem><SelectItem value="-1">Negativo (−)</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Prefisso codice conto</Label><Input placeholder="es. 60" value={f.code_prefix} onChange={(e) => setF({ ...f, code_prefix: e.target.value })} data-testid="rule-prefix-input" /></div>
          <div><Label>Parola chiave descrizione</Label><Input placeholder="es. infragruppo" value={f.keyword} onChange={(e) => setF({ ...f, keyword: e.target.value })} /></div>
          <div><Label>Natura</Label>
            <Select value={f.nature} onValueChange={(v) => setF({ ...f, nature: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="any">Qualsiasi</SelectItem><SelectItem value="costo">Costo</SelectItem><SelectItem value="ricavo">Ricavo</SelectItem><SelectItem value="patrimoniale">Patrimoniale</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label>Voce destinazione</Label>
            <Select value={f.target_row_key} onValueChange={(v) => setF({ ...f, target_row_key: v })}>
              <SelectTrigger data-testid="rule-target-select"><SelectValue /></SelectTrigger>
              <SelectContent>{rowKeys.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between"><Label>Solo righe intercompany</Label><Switch checked={f.intercompany_only} onCheckedChange={(v) => setF({ ...f, intercompany_only: v })} /></div>
          <div className="flex items-center justify-between"><Label>Attiva</Label><Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={() => onSave(f)} data-testid="rule-save-btn">Salva</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
